import React, { useContext } from 'react';
import { arrayOf, func, shape } from 'prop-types';
import { useDispatch } from 'react-redux';

import { useLocation, useNavigate } from 'react-router-dom';
import * as R from 'ramda';

// Utils
import { getSelectionURI } from 'utils/helpers/url';
import { DRAWER_TYPES } from 'utils/constants';

// Context
import { setMobileDrawerOpen } from 'feature/loading-management/mobile-drawer/store/mobileDrawerSlice';
import { setFilters } from 'feature/loading-management/filters/store/filtersSlice';
import { updateTabForGoToMyAuth } from 'feature/loading-management/my-authorizations/store/myAuthorizationsSlice';
import { DrawerContext } from 'common/drawer/DrawerContext';

// Components
import ViewPanel from 'feature/inventory-management/product-transfer-order/view-panel/ViewPanel.connector';
import EditPtoPanel from 'feature/inventory-management/product-transfer-order/edit-panel/EditPanel.connector';
import EditSupplierAuthPanel from 'feature/loading-management/supplier-authorizations/edit-panel/EditPanel.connector';
import NotificationSection from './NotificationSection';

const NotificationCenter = ({ setOpen, notifications }) => {
  const { setDrawerComponentProps } = useContext(DrawerContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const onClickOfTwoPartyExchange = ({ affiliateId, dataCompanyId, product, shortName }) => {
    // automatically filter my auth cards when navigating from notifications
    // this was not requested in the AC
    // it was done so that the product name was available to choose correct tab on initial render
    dispatch(
      setFilters({
        name: 'tempCardFilter',
        value: { product },
      })
    );
    dispatch(setMobileDrawerOpen(true));

    // check to see if we are already on the correct page
    if (
      R.both(R.includes(shortName), R.includes('/loading-management/my-authorizations'))(pathname)
    ) {
      // if we are, make sure we are on right tab
      dispatch(updateTabForGoToMyAuth(product));
    }
    // TODO: when using the notifications to route to via click
    // the url has a new affiliate, but we don't push current affiliate
    // via the click in notifications

    navigate(
      `/affiliate/${affiliateId}/${dataCompanyId}/loading-management/my-authorizations/inventory-owner/${getSelectionURI(
        shortName
      )}/`
    );
  };

  const onClick = (additionalProps, type) => {
    if (type === 'two_party_warning') return onClickOfTwoPartyExchange(additionalProps);

    const componentProps = () => {
      switch (type) {
        case 'alert':
          return {
            Component: props => (
              // Edit PTO Panel does not retrieve from Drawer Context component props, must be passed directly
              <EditPtoPanel isEdit refreshNotifications {...additionalProps} {...props} />
            ),
            type: DRAWER_TYPES.editPto,
          };
        case 'expire_warning':
          return {
            Component: props => <EditSupplierAuthPanel {...additionalProps} {...props} />,
            forceCloseSelector: R.path([
              'loadingManagement',
              'supplierAuthorizations',
              'forceCloseEditDrawer',
            ]),
            refreshNotifications: true,
          };
        default:
          return {
            Component: props => <ViewPanel {...additionalProps} {...props} />,
            refreshNotifications: true,
          };
      }
    };

    return setDrawerComponentProps({
      ...componentProps(),
    });
  };

  return (
    <NotificationSection
      handleMobileClose={() => setOpen(false)}
      notificationList={notifications}
      onClick={onClick}
    />
  );
};

NotificationCenter.propTypes = {
  notifications: arrayOf(shape({})),
  // setOpen will not be sent or used here in desktop
  setOpen: func,
};

NotificationCenter.defaultProps = {
  notifications: [],
  setOpen: () => {},
};

export default NotificationCenter;
